import React, { useState, useEffect } from 'react';
import { ArrowLeft, MoreVertical, User, Trash2, Images, RotateCcw, Flag, Ban } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { UserPresence } from './UserPresence';
import { ChatMediaGallery } from './ChatMediaGallery';
import { usePresenceTimer } from '@/hooks/usePresenceTimer';
import { useAuth } from '@/hooks/useAuth';
import { debugLogger } from '@/utils/debugLogger';
import { useImageUrl } from '@/hooks/useImageUrl';
import { chatDataService } from '@/core/services/ChatDataService';
import { toast } from 'sonner';
import { AdminService } from '@/core/services/AdminService';
import { ReportUserModal } from '@/components/shared/ReportUserModal';
import { BlockUserButton } from '@/components/shared/BlockUserButton';

interface ChatHeaderProps {
  partner?: {
    id: string;
    username?: string;
    avatar?: string;
    lastSeen?: string;
  };
  conversationId?: string;
  conversation?: {
    id: string;
    user1_id: string;
    user2_id: string;
    user1_presence_status: string | null;
    user2_presence_status: string | null;
  } | null;
  messages?: Array<{
    id: string;
    sender_id: string;
    created_at: string;
    content_type: 'text' | 'image' | 'audio';
    media_url: string | null;
    deleted_at?: string | null;
  }>;
  mediaMessages?: Array<{
    id: string;
    media_url: string;
    created_at: string;
  }>;
  realtimeConnected?: boolean;
  conversationDeletedAt?: string | null;
  conversationDeletedBy?: string | null;
  isReactivating?: boolean;
  onDeleteConversation?: () => void;
  onReactivate?: () => void;
}

export const ChatHeader: React.FC<ChatHeaderProps> = ({ 
  partner, 
  conversationId, 
  conversation,
  messages = [],
  mediaMessages = [],
  realtimeConnected = false,
  conversationDeletedAt,
  conversationDeletedBy,
  isReactivating = false,
  onDeleteConversation,
  onReactivate
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isMediaGalleryOpen, setIsMediaGalleryOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isUserAdmin, setIsUserAdmin] = useState(false);

  // Verificar se usuário é admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user?.id) return;
      
      const isAdmin = await AdminService.isAdmin(user.id);
      
      if (isAdmin) {
        setIsUserAdmin(true);
        debugLogger.debug('[ChatHeader] User is admin:', { userId: user.id });
      }
    };
    
    checkAdminStatus();
  }, [user?.id]);
  
  // Usar o novo timer de presença
  const { presenceStatus, lastSeen, mostRecentActivity } = usePresenceTimer({
    partnerId: partner?.id || null,
    conversationId,
    conversation,
    messages
  });

  const handleBack = () => {
    navigate('/main/chats');
  };

  const handleViewProfile = () => {
    if (partner?.username) {
      navigate(`/main/user/${partner.username}`);
    }
  };

  const handleDeleteConversation = async () => {
    if (!user?.id || !conversationId) return;

    try {
      const success = await chatDataService.deleteConversation(conversationId, user.id);
      
      if (success) {
        if (onDeleteConversation) {
          onDeleteConversation();
        }
        navigate('/main/chats');
      } else {
        toast.error('Erro ao excluir conversa');
      }
    } catch (error) {
      debugLogger.error('[ChatHeader] Error deleting conversation:', error);
      toast.error('Erro ao excluir conversa');
    }
  };

  const handleOpenMediaGallery = () => {
    setIsMediaGalleryOpen(true);
  };

  const handleBlockedUser = () => {
    // Navigate back to chats after blocking
    navigate('/main/chats');
  };

  // Use async hook for CDN-cached avatar URL
  const { url: avatarUrl } = useImageUrl(partner?.avatar, 'avatar');

  // Log para debug
  debugLogger.debug('[ChatHeader] Timer presence status:', {
    partnerId: partner?.id,
    presenceStatus,
    lastSeen,
    mostRecentActivity,
    realtimeConnected
  });

  return (
    <>
      <div className="fixed top-0 left-0 right-0 z-50 h-20 bg-black/95 backdrop-blur-sm border-b border-white/10 p-4 flex items-center justify-between md:left-80">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBack}
            className="text-white hover:bg-white/10 p-2"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          
          <button
            onClick={handleViewProfile}
            className="flex items-center gap-3 hover:bg-white/5 rounded-lg p-2 transition-colors"
            disabled={!partner?.username}
          >
            <Avatar className="h-12 w-12">
              <AvatarImage 
                src={avatarUrl} 
              />
              <AvatarFallback className="bg-luxury-red text-white">
                {partner?.username?.[0]?.toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
            
            <div className="flex flex-col text-left">
              <h2 className="text-white font-semibold">
                {partner?.username || 'Usuário'}
              </h2>
              <UserPresence 
                presenceStatus={presenceStatus}
                lastSeen={lastSeen}
                mostRecentActivity={mostRecentActivity}
              />
            </div>
          </button>
        </div>
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/10 p-2"
            >
              <MoreVertical className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent 
            align="end" 
            className="bg-gray-900 border-white/10 min-w-[180px]"
          >
            <DropdownMenuItem 
              onClick={handleViewProfile}
              className="text-white hover:bg-white/10 cursor-pointer"
            >
              <User className="h-4 w-4 mr-2" />
              Ver perfil
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={handleOpenMediaGallery}
              className="text-white hover:bg-white/10 cursor-pointer"
            >
              <Images className="h-4 w-4 mr-2" />
              Mídias
            </DropdownMenuItem>
            
            <DropdownMenuSeparator className="bg-white/10" />
            
            {/* Report option */}
            <DropdownMenuItem 
              onClick={() => setIsReportModalOpen(true)}
              className="text-amber-400 hover:bg-amber-500/10 cursor-pointer"
            >
              <Flag className="h-4 w-4 mr-2" />
              Denunciar
            </DropdownMenuItem>
            
            {/* Block option - using custom trigger inside menu */}
            {partner?.id && (
              <BlockUserButton 
                userId={partner.id} 
                username={partner.username}
                variant="menuItem"
                onBlocked={handleBlockedUser}
              />
            )}
            
            <DropdownMenuSeparator className="bg-white/10" />
            
            {conversationDeletedAt && onReactivate && (conversationDeletedBy === user?.id || isUserAdmin) && (
              <DropdownMenuItem 
                onClick={onReactivate}
                disabled={isReactivating}
                className="text-green-400 hover:bg-green-500/10 cursor-pointer disabled:opacity-50"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                {isReactivating 
                  ? 'Reativando...' 
                  : isUserAdmin && conversationDeletedBy !== user?.id
                    ? 'Reativar conversa (Admin)'
                    : 'Reativar conversa'
                }
              </DropdownMenuItem>
            )}
            <DropdownMenuItem 
              onClick={handleDeleteConversation}
              className="text-red-400 hover:bg-red-500/10 cursor-pointer"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Apagar conversa
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <ChatMediaGallery
        isOpen={isMediaGalleryOpen}
        onOpenChange={setIsMediaGalleryOpen}
        mediaMessages={mediaMessages}
      />

      {/* Report Modal */}
      {partner?.id && (
        <ReportUserModal
          userId={partner.id}
          username={partner.username}
          open={isReportModalOpen}
          onOpenChange={setIsReportModalOpen}
        />
      )}
    </>
  );
};
